<template>
  <div>
    <!--    label and tooltip-->
    <div
      class="label-tooltip-wrapper"
    >
      <!--      label-->
      <div
        v-if="displayLabel"
        id="autocomplete-field-label"
        :title="displayLabel"
      >
        {{ displayLabel }}
      </div>

      <!--      tooltip-->
      <v-tooltip
        v-if="hasTooltip"
        v-bind="tooltipProps"
      >
        <template
          #activator="{ on, attrs }"
        >
          <v-icon
            class="ml-2"
            :style="computedTooltipIconStyle"
            v-bind="attrs"
            v-on="on"
          >
            {{ computedTooltipIcon }}
          </v-icon>
        </template>
        <!--        Tooltip text-->
        <template
          v-if="hasTooltipText"
        >
          <span>
            {{ computedTooltipText }}
          </span>
        </template>

        <!--        Tooltip custom component-->
        <template
          v-else-if="computedTooltipCustomComponent"
        >
          <component
            :is="computedTooltipCustomComponent"
            :config="computedTooltipConfig"
          />
        </template>
      </v-tooltip>
    </div>
    <v-autocomplete
      ref="input"
      v-model="selectedItem"
      v-bind="computedProps"
      :search-input.sync="searchInput"
      @blur="handleBlur"
      @change="emitClearValidationError"
      @update:search-input="onUpdateSearchInput"
      @input.native="onInput"
    >
      <template #append-item>
        <v-list-item
          v-if="hasMoreItems && !options.hideHasMoreLabel"
          class="has-more-items"
          @click="makeSearch('', true, true)"
        >
          <v-list-item-title
            class="font-italic text-decoration-underline has-more-title"
          >
            {{ $t('base.got_more_items') }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import {
  isArray,
  isEmpty,
  isObject,
  isInteger,
  isNumber,
  isNumeric,
  isString,
  isSet,
  isFunction
} from '@/global/utils/utils'
import { api } from '@/global/services/api'
// The store is reached through `this.$store`, never imported: importing it here
// puts this component in an import cycle (the store eagerly evaluates every
// module .js through the module-directory-scanner's require.context, and those
// reach back to the field components). The cycle leaves this component
// half-initialised when FieldsRenderer registers it, so its `components` entry
// captures `undefined` and every autocomplete field silently disappears.

const DEFAULT_ITEM_VALUE = 'id'
const DEFAULT_ITEM_TEXT = 'name'
const DEFAULT_PLACEHOLDER_VALUE = ''
const DEFAULT_TOOLTIP_COLOR = '#6B7280'
const DEFAULT_TOOLTIP_ICON = 'mdi-information-slab-circle'
const DEFAULT_TOOLTIP_FONT_SIZE = '16px'
const DEFAULT_TOOLTIP_MARGIN_BOTTOM = '1px'
const DEFAULT_SEARCH_TIMEOUT = 500
export default {
  name: 'AutocompleteField',

  props: {
    appendIcon: {
      type: String,
      default: '$dropdown'
    },
    prependIcon: {
      type: String,
      default: undefined
    },
    prependInnerIcon: {
      type: String,
      default: undefined
    },
    value: {
      type: null,
      default: null
    },
    field: {
      type: Object,
      default: () => ({})
    },
    config: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({})
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // Fields are clearable unless a config/caller says otherwise.
    clearable: {
      type: Boolean,
      default: true
    },
    useFieldLabel: {
      type: Boolean,
      default: false
    },
    items: {
      type: Object,
      default: () => ({})
    },
    validationErrors: {
      type: Object,
      default: () => ({})
    },
    collectionKey: {
      type: String,
      default: ''
    },
    collectionIndex: {
      type: Number,
      default: 0
    },
    updateData: {
      type: Function,
      default: () => {}
    },
    updateValidationErrors: {
      type: Function,
      default: () => {}
    },
    customFieldKey: {
      type: String,
      default: ''
    },
    loading: {
      type: Boolean,
      default: false
    },
    removeDataPropertyByKey: {
      type: Function,
      default: () => {}
    },
    replacePropertyValueByKey: {
      type: Function,
      default: () => {}
    },
    formData: {
      type: Object,
      default: () => ({})
    },
    editMode: {
      type: Boolean,
      default: false
    },
    createMode: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'input',
    'clear-validation-errors',
    'items'
  ],

  data () {
    return {
      fetchingData: false,
      hasMoreItems: false,
      searchInput: '',
      internalItems: [],
      searchTimeoutId: null,
      calledFromPickId: false,
      pickedId: null,
      ongoingSearches: {},
      lastSearchSignature: null
    }
  },

  computed: {
    computedConfig () {
      const isValid = (val) => isSet(val) && !isEmpty(val)

      if (isValid(this.field)) return this.field
      if (isValid(this.config)) return this.config

      return {}
    },

    fieldProps () {
      return isSet(this.computedConfig?.props) && isObject(this.computedConfig.props)
        ? this.computedConfig.props
        : {}
    },

    dependantReadonly () {
      return isFunction(this.fieldProps?.dependantReadonly)
        ? this.fieldProps?.dependantReadonly({
          editState: this.editMode
        })
        : false
    },

    /**
     * Get readonly state.
     * Defaults to `false`.
     *
     * @return {boolean}
     */
    isReadonly () {
      const defaultValue = false
      return this.readonly || this.computedConfig.readonly || this.dependantReadonly || defaultValue
    },

    canEdit () {
      return !this.isReadonly && !this.disabled
    },

    selectedItem: {
      get () {
        return this.value
      },
      set (newVal) {
        this.$emit('input', newVal)
      }
    },

    processedItems () {
      const mappedItems = this.internalItems.map(item => ({
        ...item,
        displayText: this.getItemText(item)
      }))

      this.$emit('items', {
        items: mappedItems.filter(item => item.displayText)
      })
      return mappedItems.filter(item => item.displayText)
    },

    label () {
      return isSet(this.computedConfig?.label) && isString(this.computedConfig.label) ? this.computedConfig.label : ''
    },

    displayLabel () {
      return this.useFieldLabel ? '' : this.label
    },

    displayFieldLabel () {
      return this.useFieldLabel ? this.label : ''
    },

    tooltip () {
      return this.computedConfig?.tooltip
    },

    hasTooltip () {
      return !!this.tooltip
    },

    tooltipProps () {
      const props = this.tooltip && isObject(this.tooltip) ? this.tooltip : {}

      return {
        top: this.checkTooltipDirection('top'),
        left: this.checkTooltipDirection('left'),
        right: this.checkTooltipDirection('right'),
        bottom: this.checkTooltipDirection('bottom'),
        ...props
      }
    },

    computedTooltipStyle () {
      return this.tooltip?.style
    },

    computedTooltipText () {
      return this.tooltip?.text
    },

    hasTooltipText () {
      return this.computedTooltipText
    },

    computedTooltipIcon () {
      return this.tooltip?.icon || DEFAULT_TOOLTIP_ICON
    },

    computedTooltipIconStyle () {
      return this.computedTooltipStyle && isObject(this.computedTooltipStyle)
        ? this.computedTooltipStyle
        : {
            color: DEFAULT_TOOLTIP_COLOR,
            fontSize: DEFAULT_TOOLTIP_FONT_SIZE,
            marginBottom: DEFAULT_TOOLTIP_MARGIN_BOTTOM
          }
    },

    computedTooltipConfig () {
      return this.tooltip?.config
    },

    computedTooltipCustomComponent () {
      return this.tooltip?.component
    },

    computedClasses () {
      const { class: fieldClass, useDefaultEditStyle = true } = this.computedConfig || {}
      let classes = this.fieldClass ? ` ${this.fieldClass}` : ''

      if (this.editMode && this.canEdit && useDefaultEditStyle) {
        classes += ' autocomplete-field--is-editing'
      }
      if (!this.canEdit) {
        classes += ' autocomplete-field--readonly'
      }
      if (this.validationErrorsCount) {
        classes += ' autocomplete-field--has-error'
      }
      if (fieldClass && isString(fieldClass)) {
        classes += ' ' + fieldClass
      }

      classes += `  mt-${this.displayLabel ? 1 : 0}`

      return classes
    },

    itemValueKey () {
      const { form_value: formValue } = this.options || {}
      return formValue && isString(formValue) && !formValue.includes('.') ? formValue : DEFAULT_ITEM_VALUE
    },

    computedPlaceholder () {
      const { placeholder } = this.computedConfig || {}
      return placeholder && isString(placeholder) && (this.editMode || this.createMode)
        ? placeholder
        : DEFAULT_PLACEHOLDER_VALUE
    },

    computedValidationErrors () {
      let messages = []
      const { key, validationPropertyKey } = this.computedConfig || {}

      if (!key || isEmpty(this.validationErrors) || !this.canEdit) {
        return messages
      }

      for (const errorKey in this.validationErrors) {
        if (errorKey === key && !this.collectionKey) {
          messages = this.validationErrors[errorKey]
        }
        else if (errorKey === this.customFieldKey) {
          messages = this.validationErrors[errorKey]
        }
        else if (validationPropertyKey && errorKey === validationPropertyKey) {
          messages = this.validationErrors[errorKey]
        }
        else {
          const formattedCollectionKey = this.collectionKey + '.' + this.collectionIndex + '.' + key
          if (errorKey === formattedCollectionKey) {
            messages = this.validationErrors[errorKey]
          }
        }
      }

      return messages
    },

    validationErrorsCount () {
      return this.computedValidationErrors?.length || 0
    },

    shouldReturnObject () {
      return this.options?.returnValue === 'object'
    },

    computedStyle () {
      const { style: fieldStyle } = this.computedConfig || {}
      // Read-only autocompletes (`editable: false`) keep the same white
      // background as every other read-only field (text, datetime-picker) -
      // the greyed-out look was inconsistent with them. Fields that do want a
      // grey background can still pass it through their own `style`.
      let style = { backgroundColor: '#FFFFFF' }

      if (fieldStyle && isObject(fieldStyle)) {
        style = {
          ...style,
          ...fieldStyle
        }
      }

      return style
    },

    computedProps () {
      return {
        label: this.displayFieldLabel,
        class: this.computedClasses,
        items: this.processedItems,
        itemValue: this.itemValueKey,
        itemText: 'displayText',
        placeholder: this.computedPlaceholder,
        readonly: this.readonly,
        disabled: this.disabled,
        errorMessages: this.computedValidationErrors,
        errorCount: this.validationErrorsCount,
        appendIcon: this.appendIcon,
        prependIcon: this.prependIcon,
        outlined: true,
        prependInnerIcon: this.prependInnerIcon,
        loading: this.fetchingData,
        style: this.computedStyle,
        // Now that the default is `true`, an explicit `clearable: false` in the
        // field config has to be able to win - hence `??` over the prop. Vuetify
        // draws the clear icon on readonly inputs too (and it stays clickable
        // there), so the field also has to actually be editable.
        clearable: (this.computedConfig?.clearable ?? this.clearable) && this.canEdit,
        hideDetails: 'auto',
        returnObject: this.shouldReturnObject,
        menuProps: {
          contentClass: 'autocomplete-menu'
        },
        dense: true,
        ...this.fieldProps
      }
    }
  },

  watch: {
    loading: {
      handler (val) {
        this.fetchingData = !!val
      }
    },
    items: {
      immediate: true,
      deep: true,
      async handler (items) {
        if (items) {
          const { key } = this.computedConfig || {}
          // `isArray` rather than a truthiness check on the list: an empty one is an
          // answer - the field offers nothing - and has to replace what is held now
          // instead of leaving the previous options standing.
          if (key && isArray(items[key]?.data)) {
            this.internalItems = items[key].data.map(item => this.normalizeId(item))
            this.hasMoreItems = !!items[key]?.hasMore
          }
          else if (isArray(items.data)) {
            this.internalItems = items.data.map(item => this.normalizeId(item))
            this.hasMoreItems = !!items?.hasMore
          }
        }
      }
    },
    searchInput: {
      handler (val) {
        const { allowSearch, onSearchInput } = this.options || {}
        if (val === '') {
          this.searchInput = ''
          this.selectedItem = null
        }

        if (val !== null && val !== undefined && typeof val === 'string' && (allowSearch === undefined || allowSearch)) {
          clearTimeout(this.searchTimeoutId)
          this.searchTimeoutId = setTimeout(async () => {
            const foundVal = this.findSearchInputInItems(val)
            if (!foundVal) {
              await this.makeSearch(val)
            }
            if (typeof onSearchInput === 'function') {
              onSearchInput({
                val
              })
            }
          }, DEFAULT_SEARCH_TIMEOUT)
        }
      }
    },
    pickedId: {
      async handler (val) {
        if (val && isNumber(val)) {
          await this.makeSearch()
        }
      }
    },
    'computedConfig.pickedId': {
      immediate: true,
      deep: true,
      handler (val) {
        if (val && (!this.processedItems || !this.processedItems.length || !this.processedItems.find(item => item.id === val))) {
          this.pickedId = val
        }
      }
    },
    'computedConfig.autofocus': {
      immediate: true,
      handler (val) {
        if (val) {
          this.focus()
        }
      }
    },
    'computedConfig.openMenuAutomatically': {
      immediate: true,
      handler (val) {
        if (val) {
          this.openMenuAutomatically()
        }
      }
    },
    value: {
      async handler (val) {
        const { onDataLoad } = this.options || {}
        if (typeof onDataLoad === 'function') {
          onDataLoad({
            data: val,
            items: this.internalItems,
            makeSearch: (query, allItems) => this.makeSearch(query, allItems)
          })
        }
        await this.reCallSearchWithPickedId(val)
      }
    },
    'computedConfig.fetchData': {
      immediate: true,
      async handler (val) {
        if (val) {
          await this.makeSearch()
        }
      }
    }
  },

  async created () {
    const { fetchInitial } = this.options || {}
    if (fetchInitial || fetchInitial === undefined) {
      await this.makeSearch()
    }
  },

  methods: {
    isEmpty,

    checkTooltipDirection (direction) {
      const { direction: configDirection } = this.tooltip || {}
      if (!configDirection && direction === 'top') {
        return true
      }
      return direction === configDirection
    },

    reCallSearchWithPickedId (val) {
      const { id } = val || {}
      const checkId = id || val
      if (isInteger(checkId)) {
        const foundVal = this.findSearchInputInItems(checkId)
        if (!foundVal) {
          this.pickedId = checkId
        }
      }
    },

    findSearchInputInItems (val) {
      return this.processedItems?.find(obj => (this.getItemText(obj) && this.getItemText(obj) === val) || (obj?.[this.itemValueKey] === val) || (obj[DEFAULT_ITEM_TEXT] ? obj[DEFAULT_ITEM_TEXT] === val : false))
    },

    normalizeId (item) {
      const id = item.id

      if (typeof id === 'string') {
        // If a string contains any letters, keep it as-is
        if (/[a-zA-Z]/.test(id)) {
          return { ...item, id }
        }
        else {
          const parsed = parseInt(id, 10)
          return { ...item, id: isNaN(parsed) ? undefined : parsed }
        }
      }
      else if (typeof id === 'number') {
        return { ...item, id }
      }
      else {
        return { ...item, id }
      }
    },

    async handleBlur () {
      const { keepInput, onSelect, onBlur } = this.computedConfig || {}
      if (!this.internalItems.some(item => this.getItemText(item) === this.searchInput) && keepInput && typeof keepInput === 'function') {
        // If the user's input doesn't match any item, store the text directly
        const customSelectedValue = keepInput(this.searchInput, {
          items: this.internalItems
        })
        const { value, items } = customSelectedValue

        this.selectedItem = value
        this.internalItems = items
      }

      if (onSelect && typeof onSelect === 'function') {
        const callbackData = {
          selectedItem: this.selectedItem,
          instance: this.$parent?.$parent?.$parent || {},
          updateData: (data) => this.callUpdateData(data),
          updateValidationErrors: (val) => this.updateValidationErrors(val),
          removeDataPropertyByKey: (key) => this.removeDataPropertyByKey(key),
          replacePropertyValueByKey: (key, value) => this.replacePropertyValueByKey(key, value),
          searchInput: this.searchInput
        }

        onSelect(callbackData)
      }

      if (onBlur && typeof onBlur === 'function') {
        const callbackData = {
          selectedItem: this.selectedItem,
          instance: this.$parent?.$parent?.$parent || {},
          updateData: (data) => this.callUpdateData(data),
          updateValidationErrors: (val) => this.updateValidationErrors(val),
          removeDataPropertyByKey: (key) => this.removeDataPropertyByKey(key),
          replacePropertyValueByKey: (key, value) => this.replacePropertyValueByKey(key, value),
          searchInput: this.searchInput
        }

        onBlur(callbackData)
      }
    },

    callUpdateData (data) {
      if (this.updateData && typeof this.updateData === 'function') {
        this.updateData(data)
      }
    },

    async makeSearch (query = null, allItems = false, userClick = false) {
      const pickedId = this.pickedId || this.value?.id || (isNumber(this.value) ? this.value : null)

      // Create a signature so identical requests can be detected
      const signature = JSON.stringify({
        query: (typeof query === 'string' ? query : ''),
        pickedId: pickedId ?? null,
        allItems: !!allItems
      })

      // If the exact same search is already in progress, reuse it
      if (this.ongoingSearches[signature]) {
        return this.ongoingSearches[signature]
      }

      // If the exact same search was already completed and this isn't a forced "allItems" search, skip.
      if (this.lastSearchSignature === signature && !allItems) {
        return
      }

      // Store promise in the map so concurrent callers reuse it
      const promise = (async () => {
        try {
          this.fetchingData = true

          let params = {
            query: query && typeof query === 'string' ? query : '',
            pickedId: query ? null : pickedId,
            includeAll: allItems
          }

          const {
            additionalQueryParams,
            queryParams,
            module,
            route,
            onDataReceive
          } = this.options || {}

          // skip if these params not provided
          if (!module && !route) {
            if (!this.items) {
              await this.$store.dispatch('base/notifications/push', this.$t('base.autocomplete_field_wrong_config').replace(':label', this.label || '--'))
            }
            return
          }

          if (additionalQueryParams && typeof additionalQueryParams === 'function') {
            const paramsFromCallback = additionalQueryParams(this)
            params = { ...params, ...paramsFromCallback }
          }
          else if (additionalQueryParams && isObject(additionalQueryParams)) {
            params = { ...params, ...additionalQueryParams }
          }

          if (typeof queryParams === 'function') {
            const queryParamsResult = queryParams({
              instance: this,
              selectedValue: this.value,
              formData: this.formData,
              isUserClick: userClick,
              makeSearch: (q = null, a = false) => this.makeSearch(q, a)
            })

            if (queryParamsResult && isObject(queryParamsResult)) {
              params = { ...params, ...queryParamsResult }
            }
          }
          else if (queryParams && isObject(queryParams)) {
            params = { ...params, ...queryParams }
          }

          const { data, has_more: hasMore = false } = await api()[module].get(route, params)
          let normalizedData = data

          // reset local pickedId value
          if (this.pickedId) {
            this.pickedId = null
          }

          // when data is object (NOT array) with numeric keys
          // then convert it into array of objects
          if (isObject(data) && !isArray(data)) {
            const isNumericKeys = Object.keys(data).every(key => isNumeric(key))
            if (isNumericKeys) {
              normalizedData = Object.values(data)
            }
          }

          if (normalizedData?.length) {
            this.internalItems = normalizedData.map(item => this.normalizeId(item))
            this.hasMoreItems = hasMore

            if (typeof onDataReceive === 'function') {
              onDataReceive({
                normalizedData,
                hasMore,
                params
              })
            }
          }
        }
        catch (exception) {
          console.error(exception)
        }
        finally {
          this.fetchingData = false
          // mark that this signature finished
          this.lastSearchSignature = signature
          // remove from ongoing map
          delete this.ongoingSearches[signature]
        }
      })()

      this.ongoingSearches[signature] = promise
      return promise
    },

    emitClearValidationError (selectedValue) {
      const { onSelect, onChange } = this.computedConfig || {}

      if (onSelect && typeof onSelect === 'function') {
        const callbackData = {
          selectedItem: selectedValue,
          instance: this.$parent?.$parent?.$parent || {},
          updateData: (data) => this.callUpdateData(data),
          updateValidationErrors: (val) => this.updateValidationErrors(val),
          removeDataPropertyByKey: (key) => this.removeDataPropertyByKey(key),
          replacePropertyValueByKey: (key, value) => this.replacePropertyValueByKey(key, value),
          searchInput: this.searchInput
        }

        onSelect(callbackData)
      }

      if (onChange && typeof onChange === 'function') {
        const callbackData = {
          selectedItem: selectedValue,
          instance: this.$parent?.$parent?.$parent || {},
          updateData: (data) => this.callUpdateData(data),
          updateValidationErrors: (val) => this.updateValidationErrors(val),
          removeDataPropertyByKey: (key) => this.removeDataPropertyByKey(key),
          replacePropertyValueByKey: (key, value) => this.replacePropertyValueByKey(key, value),
          searchInput: this.searchInput
        }

        onChange(callbackData)
      }

      this.$emit('clear-validation-errors')
    },

    onInput (val) {
      this.$emit('clear-validation-errors')
      const { onTyping } = this.computedConfig || {}

      if (typeof onTyping === 'function') {
        onTyping({
          value: val,
          updateData: (val) => this.updateData(val)
        })
      }
    },

    onUpdateSearchInput (val) {
      const { onInput } = this.computedConfig || {}

      if (typeof onInput === 'function') {
        onInput({
          value: val,
          updateData: (val) => this.updateData(val)
        })
      }
    },

    focus () {
      this.$nextTick(() => {
        const element = this.$refs.input.$el.querySelector('input')
        if (element) {
          setTimeout(() => {
            element.focus()
          }, 100)
        }
      })
    },

    openMenuAutomatically () {
      this.$nextTick(() => {
        const element = this.$refs.input.$el.querySelector('input')
        if (element) {
          element.click()
        }
      })
    },

    getItemText (item) {
      let itemText = item?.[DEFAULT_ITEM_TEXT]

      if (this.options && this.options.custom_display_value) {
        itemText = this.options.custom_display_value(item)
      }

      return itemText && typeof itemText === 'string' ? itemText : undefined
    }
  }
}
</script>

<style scoped lang="scss">
.has-more-items {
  cursor: pointer !important;
}
::v-deep.v-input > .v-input__control > .v-input__slot > .v-select__slot > label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
}

::v-deep {
  // Disabled fields keep the normal text colour, same as TextField: they mostly
  // render an already-picked value the user isn't editing right now, and greying
  // that out reads as "empty" rather than "not editable".
  // A disabled autocomplete shows its value in `.v-select__selections` (the
  // search `input` only holds text while the field has focus), and Vuetify greys
  // both out - hence the two selectors.
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > input,
  .v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > .v-select__selections {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #111827 !important;
    font-weight: 500 !important;
  }

  .v-text-field--outlined fieldset {
    border-color: #E5E7EB !important;
    border-radius: 8px !important;
  }

  .autocomplete-field--is-editing fieldset {
    border-color: #65AD45 !important;
  }
  .v-input > .v-input__control > .v-input__slot > .v-select__slot > input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #111827;
    font-weight: 500;
  }

  .autocomplete-field--readonly .v-input__control > .v-input__slot > .v-select__slot > input {
    font-family: 'Satoshi', sans-serif;
    font-size: 14px !important;
    line-height: 20px !important;
    color: #6B7280 !important;
    font-weight: 400 !important;
  }

  // Scoped to the input box itself (`.v-input__slot`), not the whole
  // `.v-input` - that also contains `.v-text-field__details` (the
  // validation message row below), which would otherwise get painted
  // green too and look like the field stretched down over the error text.
  .autocomplete-field--is-editing > .v-input__control > .v-input__slot {
    background-color: #F7FEE7 !important;
  }

  // Placed after the default/editing border rules above so it wins the
  // (equal-specificity) cascade regardless of edit state.
  .autocomplete-field--has-error fieldset {
    border-color: #DC2626 !important;
  }
}

@media (max-width: 960px) {
  ::v-deep {
    .v-input > .v-input__control > .v-input__slot > .v-select__slot > input,
    .v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > input,
    .v-input--is-disabled > .v-input__control > .v-input__slot > .v-select__slot > .v-select__selections,
    .autocomplete-field--readonly .v-input__control > .v-input__slot > .v-select__slot > input {
      font-size: 16px !important;
      line-height: 24px !important;
    }
  }
}

#autocomplete-field-label {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px;
  color: #111827;
  line-height: 20px;
  font-weight: 500;
  // Never wrap - see TextField. `min-width: 0` is needed because this is a flex
  // item inside `.label-tooltip-wrapper`.
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.has-more-title {
  font-family: 'Satoshi', sans-serif;
  font-size: 14px !important;
  line-height: 20px !important;
  font-weight: 900 !important;
}

.label-tooltip-wrapper {
  display: flex;
}
</style>
